import { useState, useEffect, useCallback } from 'react'
import type { Provider } from '../services/chatService'

export interface ZenSettings {
  defaultProvider: Provider | null
  autoSaveEnabled: boolean
  debugMode: boolean
  theme: 'zen' | 'minimal' | 'focus'
  aiResponseSpeed: 'balanced' | 'fast' | 'quality'
  autoDetectLanguage: boolean
  // Admin-only settings
  enableLanguagePairPrompts: boolean  // Use lens guides for 2-3x speedup
  enablePromptCaching: boolean         // Use Anthropic prompt caching
}

const defaultSettings: ZenSettings = {
  defaultProvider: null,
  autoSaveEnabled: true,
  debugMode: false,
  theme: 'zen',
  aiResponseSpeed: 'fast',
  autoDetectLanguage: true,
  // Admin defaults
  enableLanguagePairPrompts: true,  // Enabled by default for better quality
  enablePromptCaching: true          // Enabled by default for cost savings
}

const SETTINGS_KEY = 'zenCreatorSettings'

export const useZenSettings = () => {
  const [settings, setSettings] = useState<ZenSettings>(defaultSettings)
  const [isLoading, setIsLoading] = useState(true)

  // Load settings from localStorage
  const loadSettings = useCallback(() => {
    try {
      const savedSettings = localStorage.getItem(SETTINGS_KEY)
      console.log('🔍 [useZenSettings] Loading from localStorage:', {
        raw: savedSettings,
        defaults: defaultSettings
      })

      if (savedSettings) {
        const parsed = JSON.parse(savedSettings)
        const merged = { ...defaultSettings, ...parsed }
        console.log('✅ [useZenSettings] Loaded and merged settings:', {
          parsed,
          merged,
          enableLanguagePairPrompts: merged.enableLanguagePairPrompts,
          enablePromptCaching: merged.enablePromptCaching
        })
        setSettings(merged)
      } else {
        console.log('⚠️ [useZenSettings] No saved settings found, using defaults:', defaultSettings)
        setSettings(defaultSettings)
      }
    } catch (error) {
      console.warn('❌ [useZenSettings] Failed to load Zen settings:', error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Save settings to localStorage
  const saveSettings = useCallback((newSettings: Partial<ZenSettings>) => {
    const updatedSettings = { ...settings, ...newSettings }
    console.log('💾 [useZenSettings] Saving settings:', {
      newSettings,
      currentSettings: settings,
      updatedSettings,
      enableLanguagePairPrompts: updatedSettings.enableLanguagePairPrompts,
      enablePromptCaching: updatedSettings.enablePromptCaching
    })
    setSettings(updatedSettings)
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(updatedSettings))
    console.log('✅ [useZenSettings] Saved to localStorage:', localStorage.getItem(SETTINGS_KEY))
  }, [settings])

  // Update specific setting
  const updateSetting = useCallback(<K extends keyof ZenSettings>(
    key: K,
    value: ZenSettings[K]
  ) => {
    saveSettings({ [key]: value })
  }, [saveSettings])

  // Reset to defaults
  const resetSettings = useCallback(() => {
    setSettings(defaultSettings)
    localStorage.removeItem(SETTINGS_KEY)
  }, [])

  // Get preferred provider based on speed setting
  const getPreferredProvider = useCallback((providers: Provider[]): Provider | null => {
    if (providers.length === 0) return null

    // If user has explicitly set a default provider, use it
    if (settings.defaultProvider) {
      const found = providers.find(p => 
        p.provider === settings.defaultProvider?.provider && 
        p.model === settings.defaultProvider?.model
      )
      if (found) return found
    }

    // Otherwise, apply speed preference logic
    switch (settings.aiResponseSpeed) {
      case 'fast': {
        // Prioritize Cerebras models
        const cerebrasModels = providers.filter(p => 
          p.status?.includes('Cerebras') || p.status?.includes('Ultra-Fast')
        )
        return cerebrasModels[0] || providers[0]
      }
      case 'quality': {
        // Prioritize Claude or GPT models
        const qualityModels = providers.filter(p => 
          p.model.includes('claude') || p.model.includes('gpt-4')
        )
        return qualityModels[0] || providers[0]
      }
      case 'balanced':
      default: {
        // Use first available provider
        return providers[0]
      }
    }
  }, [settings])

  // Check if Cerebras should be preferred
  const shouldPreferCerebras = useCallback(() => {
    return settings.aiResponseSpeed === 'fast'
  }, [settings.aiResponseSpeed])

  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute('data-zen-theme', settings.theme)
  }, [settings.theme])

  // Load settings on mount
  useEffect(() => {
    loadSettings()
  }, [loadSettings])

  return {
    settings,
    isLoading,
    saveSettings,
    updateSetting,
    resetSettings,
    getPreferredProvider,
    shouldPreferCerebras,
    // Convenience getters
    defaultProvider: settings.defaultProvider,
    autoSaveEnabled: settings.autoSaveEnabled,
    debugMode: settings.debugMode,
    theme: settings.theme,
    aiResponseSpeed: settings.aiResponseSpeed,
    autoDetectLanguage: settings.autoDetectLanguage,
    enableLanguagePairPrompts: settings.enableLanguagePairPrompts,
    enablePromptCaching: settings.enablePromptCaching
  }
}

export default useZenSettings